import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Accordion } from 'react-bootstrap'

import { getIntegrationDisplayName, INTEGRATIONS_WITH_DYNAMIC_SCHEMA, IntegrationServiceId } from '../../../common/integrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { Button } from '../../../components/Button/Button'

import { deselectKeys, setSelectedKeys } from '../../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentProspectingDetailsFormLoading,
  selectDocumentProspectingDetailsFormProps,
  selectDocumentProspectingDetailsFormValues,
  selectDocumentProspectingIntegrationContactErrors,
  selectDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import {
  DocumentProspectingDetailsFormProps,
  DocumentProspectingStep,
  DocumentProspectingStepProps,
  ProspectingDetailsFormValues,
  SegmentedDocumentProspectingDetailsFormProps,
  SegmentedProspectingDetailsFormValues,
} from '../../../models/DocumentProspecting'

import { SegmentedExternalContactUpdateViewElement } from '../../UpdateExternalContact/SegmentedExternalContactUpdateViewElement'
import { UpdateExternalContactFormV2 } from '../../UpdateExternalContact/UpdateExternalContactFormV2'
import { gatherAllChildSuccessfulFields, getAllKeysInData } from '../../UpdateExternalContact/utils'

import { getAggregatedSchemaForIntegrationWithSegmentedSchema, getAggregatedValuesForIntegrationWithSegmentedSchema } from './utils'
import { useProspectingDetailsForm } from './hooks'

const _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID = 'data'

const _CURRENT_STEP = DocumentProspectingStep.DETAILS_FORM

interface ProspectingDetailsFormRef {
  submit: () => Promise<void>
}

export const ProspectingDetailsForm: React.FC<DocumentProspectingStepProps> = ({ activeStep, onComplete: _onComplete }) => {
  const { tracked } = useTracking()

  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const values = useAppSelector(selectDocumentProspectingDetailsFormValues)
  const props = useAppSelector(selectDocumentProspectingDetailsFormProps)
  const isLoading = useAppSelector(selectDocumentProspectingDetailsFormLoading)

  const formRef = useRef<ProspectingDetailsFormRef | null>(null)

  const { submitFormValues, submitLoading } = useProspectingDetailsForm()

  const isActive = activeStep === _CURRENT_STEP

  const onComplete = tracked(_onComplete, { eventName: TrackEvent.DOCUMENT_PROSPECTING_COMPLETED })

  const onSubmit = useCallback(
    async (detailsFormValues: ProspectingDetailsFormValues) => {
      const isSuccess = await submitFormValues(detailsFormValues)
      if (!isSuccess) {
        return
      }
      onComplete()
    },
    [onComplete, submitFormValues],
  )

  const _handleSubmit = async () => {
    if (!formRef.current) {
      return
    }
    await formRef.current.submit()
  }

  const handleSubmit = tracked(_handleSubmit, { eventName: TrackEvent.DOCUMENT_PROSPECTING_FIELDS_CONFIRMED })

  if (!isActive) {
    return (
      <div className='d-flex align-items-center gap-3'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div className='document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center zocks-border-color-gray-200 zocks-bg-color-gray-100 border'>
            <i className='bi bi-list-check zocks-text-gray-500' />
          </div>
        </div>
        <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
          <h5 className='zocks-text-gray-500'>Select fields to save</h5>
        </div>
      </div>
    )
  }

  if (!serviceId) {
    return null
  }

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex gap-3 align-items-start'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div className='document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center zocks-border-color-gray-200 zocks-bg-color-gray-100 border'>
            <i className='bi bi-list-check' />
          </div>
        </div>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
            <h5>Select fields to save</h5>
          </div>
          <span className='paragraph-small'>Choose and review fields for {integrationDisplayName}.</span>
        </div>
      </div>
      <div className='d-flex flex-column gap-4 zocks-pl-3.0'>
        {!values || !props || isLoading ? (
          <div className='skeleton d-flex flex-column align-items-center justify-content-center zocks-py-4.0'>
            <span className='paragraph-medium'>Loading…</span>
          </div>
        ) : (
          <ProspectingDetailsFormContent ref={formRef} serviceId={serviceId} values={values} props={props} onSubmit={onSubmit} />
        )}
      </div>
      <div className='d-flex align-items-center gap-3'>
        <div className='z-1'>
          <div className='document-prospecting-step-icon-wrapper d-flex align-items-center justify-content-center'>
            <div className='document-prospecting-final-step-icon zocks-bg-color-gray-300 rounded-circle' />
          </div>
        </div>
        <Button variant='primary' onClick={handleSubmit} loading={submitLoading} disabled={isLoading}>
          {INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId) ? 'Update contact' : 'Create contact'}
        </Button>
      </div>
    </div>
  )
}

interface ProspectingDetailsFormContentProps {
  serviceId: IntegrationServiceId
  values: ProspectingDetailsFormValues
  props: DocumentProspectingDetailsFormProps
  onSubmit: (detailsFormValues: ProspectingDetailsFormValues) => void
}

const ProspectingDetailsFormContent = forwardRef<ProspectingDetailsFormRef, ProspectingDetailsFormContentProps>(
  ({ serviceId, values, props, onSubmit }, ref) => {
    const dispatch = useAppDispatch()

    const form = useForm<ProspectingDetailsFormValues>({ defaultValues: values })

    useImperativeHandle(ref, () => ({ submit: form.handleSubmit(onSubmit) }))

    useEffect(() => {
      dispatch(setSelectedKeys(getAllKeysInData(values.data, _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID)))
    }, [dispatch, values])

    return (
      <FormProvider {...form}>
        {props.type === 'segmented' && values.type === 'segmented' ? (
          <SegmentedProspectingDetailsFormContent serviceId={serviceId} values={values} props={props} />
        ) : (
          <UpdateExternalContactFormV2
            serviceId={serviceId}
            rootFieldId={_PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID}
            contactSchema={props.schema}
            previousContactValues={props.previousValues}
            contactValues={values.data}
            requiredSteps={{}}
            metadata={{}}
            hidePreviousValues={true}
            hideRootFieldHeader={true}
          />
        )}
      </FormProvider>
    )
  },
)

interface SegmentedProspectingDetailsFormContentProps {
  serviceId: IntegrationServiceId
  values: SegmentedProspectingDetailsFormValues
  props: SegmentedDocumentProspectingDetailsFormProps
}

const SegmentedProspectingDetailsFormContent: React.FC<SegmentedProspectingDetailsFormContentProps> = ({ serviceId, values, props }) => {
  const dispatch = useAppDispatch()

  const errors = useAppSelector(selectDocumentProspectingIntegrationContactErrors)

  const [activeKey, setActiveKey] = useState<string>()

  const { data } = values
  const { schema, previousValues } = props

  const aggregatedSchema = useMemo(() => getAggregatedSchemaForIntegrationWithSegmentedSchema(schema), [schema])
  const aggregatedPreviousValues = useMemo(() => getAggregatedValuesForIntegrationWithSegmentedSchema(previousValues), [previousValues])

  useEffect(() => {
    const fieldsToDeselectArray = Object.entries(schema).map(([key, panel]) =>
      gatherAllChildSuccessfulFields(data[key], key, panel, errors, Object.keys(data[key] ?? {}), _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID),
    )
    const fieldsToDeselect = fieldsToDeselectArray.flat()
    dispatch(deselectKeys(fieldsToDeselect))
  }, [data, dispatch, errors, schema])

  return (
    <div className='d-flex flex-column gap-3'>
      {errors ? (
        <div className='d-flex align-items-center gap-3 p-3 rounded-3 zocks-bg-color-warning'>
          <i className='bi bi-exclamation-triangle fs-5' />
          <p className='paragraph-medium m-0'>
            <span className='bold'>Contact partially saved. </span>
            <span>Some fields couldn't be synced to eMoney due to dependencies. Review sections marked with red and try again.</span>
          </p>
        </div>
      ) : null}
      <Accordion activeKey={activeKey} onSelect={(e) => setActiveKey(`${e}`)}>
        {Object.entries(schema).map(([key, panel]) => {
          return (
            <SegmentedExternalContactUpdateViewElement
              key={key}
              serviceId={serviceId}
              panel={panel}
              panelKey={key}
              metadata={errors ?? {}}
              contactSchema={aggregatedSchema}
              previousContactValues={aggregatedPreviousValues}
              contactValues={data}
              rootFieldId={_PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID}
              hidePreviousValues={true}
              hideRootFieldHeader={true}
            />
          )
        })}
      </Accordion>
    </div>
  )
}
